import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, CurrencyDollar } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

const STAGES = ["target", "pitched", "negotiating", "contracted", "fulfilled", "renewal"];

export default function Sponsors() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({
    name: "", contact: "", mission_alignment: 5,
    estimated_value: 0, contracted_value: 0,
    stage: "target", deliverables: "", next_action: "",
  });

  const load = async () => setItems((await api.get("/sponsors")).data);
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/sponsors", { ...f, estimated_value: +f.estimated_value, contracted_value: +f.contracted_value });
    toast.success("Sponsor added");
    setShowForm(false);
    setF({ ...f, name: "", contact: "", deliverables: "", next_action: "" });
    load();
  };

  const move = async (id, stage) => {
    await api.patch(`/sponsors/${id}`, { stage });
    load();
  };

  const totalEst = items.reduce((s, x) => s + (x.estimated_value || 0), 0);
  const totalContracted = items.reduce((s, x) => s + (x.contracted_value || 0), 0);
  const grouped = STAGES.map((s) => ({ stage: s, items: items.filter((x) => x.stage === s) }));

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <PageHeader
        overline="CRM · Revenue Pipeline"
        title="Sponsors"
        subtitle="From target → contracted. Track dollars in motion."
        actions={<button data-testid="new-sponsor-btn" onClick={() => setShowForm(true)} className="btn-primary text-sm flex items-center gap-1"><Plus size={14} /> New sponsor</button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="oz-card p-4">
          <div className="overline mb-1">Pipeline value</div>
          <div className="font-mono text-2xl">${totalEst.toLocaleString()}</div>
        </div>
        <div className="oz-card p-4">
          <div className="overline mb-1">Contracted</div>
          <div className="font-mono text-2xl text-emerald-400">${totalContracted.toLocaleString()}</div>
        </div>
        <div className="oz-card p-4">
          <div className="overline mb-1">Active accounts</div>
          <div className="font-mono text-2xl">{items.filter((s) => s.stage === "contracted").length}</div>
        </div>
        <div className="oz-card p-4">
          <div className="overline mb-1">In negotiation</div>
          <div className="font-mono text-2xl text-amber-400">{items.filter((s) => s.stage === "negotiating").length}</div>
        </div>
      </div>

      {showForm && (
        <div className="oz-card p-5 mb-6">
          <form onSubmit={create} className="grid md:grid-cols-2 gap-3">
            <input required placeholder="Sponsor name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <input placeholder="Contact" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <input type="number" placeholder="Estimated value $" value={f.estimated_value} onChange={(e) => setF({ ...f, estimated_value: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm font-mono" />
            <input type="number" placeholder="Contracted value $" value={f.contracted_value} onChange={(e) => setF({ ...f, contracted_value: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm font-mono" />
            <select value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm">
              {STAGES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <label className="text-xs text-[color:var(--oz-text-dim)]">Mission {f.mission_alignment}
              <input type="range" min="1" max="10" value={f.mission_alignment} onChange={(e) => setF({ ...f, mission_alignment: +e.target.value })} className="w-full" />
            </label>
            <input placeholder="Deliverables" value={f.deliverables} onChange={(e) => setF({ ...f, deliverables: e.target.value })} className="col-span-2 bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <input placeholder="Next action" value={f.next_action} onChange={(e) => setF({ ...f, next_action: e.target.value })} className="col-span-2 bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <div className="col-span-2 flex gap-2">
              <button className="btn-primary text-sm">Save</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {grouped.map((col) => (
          <div key={col.stage} className="kanban-col p-3" data-testid={`sponsor-col-${col.stage}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-[10px]">{col.stage}</div>
              <div className="text-xs font-mono text-[color:var(--oz-text-muted)]">
                ${col.items.reduce((s, x) => s + (x.stage === "contracted" ? x.contracted_value : x.estimated_value), 0).toLocaleString()}
              </div>
            </div>
            <div className="space-y-2">
              {col.items.map((s) => (
                <div key={s.id} className="p-3 rounded-sm border border-[color:var(--oz-border)] bg-[color:var(--oz-page)] hover:border-[color:var(--oz-emerald)] transition-colors" data-testid={`sponsor-${s.id}`}>
                  <div className="text-sm mb-1">{s.name}</div>
                  <div className="text-[11px] text-[color:var(--oz-text-muted)] mb-2">{s.contact}</div>
                  <div className="flex items-center gap-1 mb-2">
                    <CurrencyDollar size={12} color="var(--oz-gold)" />
                    <div className="font-mono text-xs text-[color:var(--oz-gold)]">
                      {(s.stage === "contracted" ? s.contracted_value : s.estimated_value)?.toLocaleString()}
                    </div>
                  </div>
                  {s.next_action && <div className="text-[11px] text-[color:var(--oz-text-dim)] mb-2 italic">→ {s.next_action}</div>}
                  <select value={s.stage} onChange={(e) => move(s.id, e.target.value)} className="w-full bg-transparent border border-[color:var(--oz-border)] px-2 py-1 text-[10px] rounded-sm font-mono">
                    {STAGES.map((st) => <option key={st}>{st}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
